import { useState, useEffect } from 'react';
import Modal from '../modal/modal';
import Card from '../card/Card';
import Input from '../input/Input';
import CurrencyInput from '../input/CurrencyInput';
import DropDown from '../dropdown/DropDown';
import P from '../texts/p/P';
import { useToast } from '../toast/toast';
import { 
  getContributionAssetIntegrationFormMetadata, 
  createContributionAssetIntegration 
} from '../../services/GuaranteesService';

interface AssetOption {
  id: number;
  name: string;
}

interface CurrencyOption {
  id: number;
  name: string;
}

interface FormMetadata {
  contributions: AssetOption[];
  assets_to_integrate: AssetOption[];
  currencies: CurrencyOption[];
}

interface IntegrateContributionModalProps {
  isOpen: boolean;
  onClose: () => void;
  contributionId: number;
  amount: number;
  integrated_amount: number;
  currency_name: string;
  onSuccess?: () => void;
}

const IntegrateContributionModal = ({
  isOpen,
  onClose,
  contributionId,
  amount,
  integrated_amount,
  currency_name,
  onSuccess,
}: IntegrateContributionModalProps) => {
  const [formMetadata, setFormMetadata] = useState<FormMetadata>({
    contributions: [],
    assets_to_integrate: [],
    currencies: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);

  // Form values
  const [assetToIntegrateId, setAssetToIntegrateId] = useState<number | null>(null);
  const [nominalValue, setNominalValue] = useState<string>('');
  const [price, setPrice] = useState<number | null>(null);
  const [currencyId, setCurrencyId] = useState<number | null>(null);
  const [amountError, setAmountError] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      loadFormMetadata();
    }
  }, [isOpen]);

  const loadFormMetadata = async () => {
    setIsLoadingMetadata(true);
    try {
      const response = await getContributionAssetIntegrationFormMetadata();
      if (response.success) {
        if ('data' in response && response.data) {
          setFormMetadata(response.data);
          // Preseleccionar la moneda basada en currency_name
          const matchingCurrency = response.data.currencies?.find(
            (currency) => currency.name?.toUpperCase() === currency_name?.toUpperCase()
          );
          if (matchingCurrency) {
            setCurrencyId(matchingCurrency.id);
          }
        }
      } else {
        const errorMsg = 'message' in response ? response.message : 'Error al cargar datos del formulario';
        useToast.error({
          title: 'Error',
          description: errorMsg,
          id: 'default-toast',
          autoClose: 5000,
          closeButton: true,
        });
      }
    } catch (error) {
      console.error('Error loading form metadata:', error);
      useToast.error({
        title: 'Error',
        description: 'Error al cargar datos del formulario',
        id: 'default-toast',
        autoClose: 5000,
        closeButton: true,
      });
    } finally {
      setIsLoadingMetadata(false);
    }
  };

  const resetForm = () => {
    setAssetToIntegrateId(null);
    setNominalValue('');
    setPrice(null);
    setCurrencyId(null);
    setAmountError('');
  };

  const parseExceedIntegrationError = (errorMessage: string): string | null => {
    // Pattern: "The sum of integrations (price * nominal_value = X) would exceed the contribution amount (Y)"
    const pattern = /The sum of integrations \(price \* nominal_value = ([\d.]+)\) would exceed the contribution amount \(([\d.]+)\)/;
    const match = errorMessage.match(pattern);
    
    if (match) {
      const totalIntegration = parseFloat(match[1]);
      const contributionAmount = parseFloat(match[2]);
      const availableAmount = amount - integrated_amount;
      
      // Format numbers with proper decimal places
      const formattedTotal = totalIntegration.toLocaleString('es-AR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      const formattedAvailable = availableAmount.toLocaleString('es-AR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      
      return `El monto total de la integración (${formattedTotal}) excede el monto disponible (${formattedAvailable})`;
    }
    
    return null;
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    // Validations
    if (!assetToIntegrateId) {
      useToast.error({
        title: 'Error',
        description: 'Seleccione un activo a integrar',
        id: 'default-toast',
        autoClose: 5000,
        closeButton: true,
      });
      return;
    }

    if (!nominalValue || isNaN(parseFloat(nominalValue)) || parseFloat(nominalValue) <= 0) {
      useToast.error({
        title: 'Error',
        description: 'Ingrese un valor nominal válido',
        id: 'default-toast',
        autoClose: 5000,
        closeButton: true,
      });
      return;
    }

    if (price === null || price <= 0) {
      useToast.error({
        title: 'Error',
        description: 'Ingrese un precio válido',
        id: 'default-toast',
        autoClose: 5000,
        closeButton: true,
      });
      return;
    }

    if (!currencyId) {
      useToast.error({
        title: 'Error',
        description: 'Seleccione una moneda',
        id: 'default-toast',
        autoClose: 5000,
        closeButton: true,
      });
      return;
    }

    // Validar que el valor nominal * precio no exceda el monto disponible
    const totalIntegration = parseFloat(nominalValue) * price;
    const availableAmount = amount - integrated_amount;
    
    if (totalIntegration > availableAmount) {
      setAmountError(
        `El monto total de la integración (${totalIntegration.toLocaleString('es-AR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}) excede el monto disponible (${availableAmount.toLocaleString('es-AR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })})`
      );
      return;
    }
    
    // Limpiar error si la validación pasó
    setAmountError('');

    setIsLoading(true);
    try {
      const requestBody = {
        contribution_id: contributionId,
        asset_to_integrate_id: assetToIntegrateId,
        nominal_value: parseFloat(nominalValue),
        price: price,
        currency_id: currencyId,
      };

      const response = await createContributionAssetIntegration(requestBody);
      
      if (response.success) {
        useToast.success({
          title: 'Éxito',
          description: 'Integración de activo creada exitosamente',
          id: 'default-toast',
          autoClose: 5000,
          closeButton: true,
        });
        resetForm();
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        // Handle error response
        const errorMessage = ('message' in response ? response.message : 'Error al crear integración de activo') || 'Error al crear integración de activo';
        const translatedMessage = parseExceedIntegrationError(errorMessage);
        
        // Show error in the same place as local validation (above buttons)
        if (translatedMessage) {
          setAmountError(translatedMessage);
        } else {
          setAmountError(errorMessage);
        }
      }
    } catch (error: any) {
      console.error('Error creating contribution asset integration:', error);
      
      // Try to extract error message from exception
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Error al crear integración de activo';
      
      const translatedMessage = parseExceedIntegrationError(errorMessage);
      
      // Show error in the same place as local validation (above buttons)
      if (translatedMessage) {
        setAmountError(translatedMessage);
      } else {
        setAmountError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Dropdown helpers for Activo a integrar
  const assetItems = (formMetadata.assets_to_integrate || []).map(asset => ({
    label: asset.name,
    disabled: false
  }));

  const getSelectedAssetName = () => {
    const selected = formMetadata.assets_to_integrate?.find(asset => asset.id === assetToIntegrateId);
    return selected ? selected.name : 'Seleccionar';
  };

  const handleAssetChange = (selectedLabel: string) => {
    const asset = formMetadata.assets_to_integrate?.find(asset => asset.name === selectedLabel);
    if (asset) {
      setAssetToIntegrateId(asset.id);
    }
  };

  // Dropdown helpers for Moneda
  const currencyItems = (formMetadata.currencies || []).map(currency => ({
    label: currency.name,
    disabled: false
  }));

  const getSelectedCurrencyName = () => {
    const selected = formMetadata.currencies?.find(currency => currency.id === currencyId);
    return selected ? selected.name : 'Seleccionar';
  };

  const handleCurrencyChange = (selectedLabel: string) => {
    const currency = formMetadata.currencies?.find(currency => currency.name === selectedLabel);
    if (currency) {
      setCurrencyId(currency.id);
    }
  };

  // Verificar si todos los campos requeridos están completos
  const isFormValid = () => {
    return (
      assetToIntegrateId !== null &&
      nominalValue !== '' &&
      !isNaN(parseFloat(nominalValue)) &&
      parseFloat(nominalValue) > 0 &&
      price !== null &&
      price > 0 &&
      currencyId !== null
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Integrar aporte"
      width='600px'
      primaryText='Confirmar'
      primaryVariant='primary'
      onPrimaryAction={handleSubmit}
      primaryActionDisabled={isLoading || isLoadingMetadata || !isFormValid()}
      secondaryText='Cancelar'
      secondaryVariant='contained'
      onSecondaryAction={handleClose}
      showOverlay={true}
    >
      <Card margin={{ top: '4' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
          {/* Activo a integrar */}
          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='strong'>
              Activo a integrar *
            </P>
            <Card margin={{ top: '2' }}>
              <DropDown
                id="asset_to_integrate"
                title={getSelectedAssetName()}
                items={assetItems}
                onChange={handleAssetChange}
                size="md"
                disabled={isLoadingMetadata}
              />
            </Card>
          </Card>

          {/* Valor nominal */}
          <Input
            label="Valor nominal *"
            name="nominal_value"
            type="number"
            value={nominalValue}
            onChange={(e) => setNominalValue(e.target.value)}
            disabled={isLoading || isLoadingMetadata}
            size="md"
          />

          {/* Precio */}
          <CurrencyInput
            label="Precio *"
            name="price"
            value={price}
            onValueChange={(val) => setPrice(val)}
            disabled={isLoading || isLoadingMetadata}
            size="md"
          />

          {/* Moneda */}
          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='strong'>
              Moneda *
            </P>
            <Card margin={{ top: '2' }}>
              <DropDown
                id="currency"
                title={getSelectedCurrencyName()}
                items={currencyItems}
                onChange={handleCurrencyChange}
                size="md"
                disabled={true}
              />
            </Card>
          </Card>

          {/* Mensaje de error */}
          {amountError && (
            <Card>
              <P withMargin={false} variant='danger' type='3' modifier='regular'>
                {amountError}
              </P>
            </Card>
          )}
        </div>
      </Card>
    </Modal>
  );
};

export default IntegrateContributionModal;
